import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, Reorder } from 'motion/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  ShieldAlert, Plus, Calendar, Image as ImageIcon,
  X, Loader2, MoreVertical, Edit2, Archive, Trash2,
  ChevronDown, ChevronUp, Utensils, RefreshCw, Coins,
  GripVertical
} from 'lucide-react';
import {
  collection, addDoc, updateDoc, deleteDoc, getDocs, doc,
  serverTimestamp, Timestamp, writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDbSafe, getStorageSafe } from '../services/firebaseConfig';
import { useYapDay } from '../contexts/YapDayContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FoodSection {
  emoji: string;
  heading: string;   // e.g. "About Pilau"  — label is fixed, content is editable
  content: string;   // free text / bullet lines
}

export interface AfroEvent {
  id: string;
  title: string;
  description: string;   // subtitle / one-liner shown under main title
  imageUrl: string;
  foodName?: string;
  template?: 'food_of_week' | 'custom';
  // Structured Food-of-Week sections (stored as JSON string in Firestore)
  foodSections?: FoodSection[];
  startsAt: string;
  expiresAt: string;
  status: 'active' | 'archived';
  createdBy: string;
  order?: number;
}

interface LegionEventManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  moderatorId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toIso = (ts: any): string => {
  if (!ts) return '';
  if (ts?.toDate) return ts.toDate().toISOString();
  return String(ts);
};

const defaultExpiryDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
};

// Default 4 sections for Food of the Week
const DEFAULT_FOOD_SECTIONS = (foodName = ''): FoodSection[] => [
  { emoji: '🍽️', heading: `About ${foodName || '[Food Name]'}`, content: '' },
  { emoji: '🌿', heading: "How It's Made", content: '' },
  { emoji: '🌍', heading: 'Cultural Background', content: '' },
  { emoji: '📍', heading: "Where It's Enjoyed", content: '' },
];

const EMPTY_EVENT = (): Partial<AfroEvent> => ({
  title: '',
  description: '',
  imageUrl: '',
  expiresAt: defaultExpiryDate(),
  status: 'active',
  template: 'custom',
});

const FOOD_TEMPLATE = (foodName = ''): Partial<AfroEvent> => ({
  title: `Food of the Week: ${foodName}`,
  foodName,
  description: '',
  imageUrl: '',
  foodSections: DEFAULT_FOOD_SECTIONS(foodName),
  expiresAt: defaultExpiryDate(),
  status: 'active',
  template: 'food_of_week',
});

// ─── Component ────────────────────────────────────────────────────────────────

export function LegionEventManagerModal({
  isOpen, onClose, moderatorId,
}: LegionEventManagerModalProps) {
  const [events, setEvents] = useState<AfroEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Partial<AfroEvent> | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper to compute the exact end of YapDay for a given local date string (YYYY-MM-DD)
  // YapDay ends at 12:00 AM EST (UTC-5)
  const getYapDayEndForDate = (dateStr: string) => {
    if (!dateStr) return new Date();
    // Start with the selected date strictly in UTC to avoid local timezone jumping
    const [y, m, d] = dateStr.split('-').map(Number);
    // YapDay boundaries are 5:00 AM UTC (Midnight EST)
    // To end on e.g. "April 6th's YapDay", the boundary is April 7th at 05:00 UTC.
    const dateUTC = Date.UTC(y, m - 1, d + 1, 5, 0, 0);
    return new Date(dateUTC);
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────────
  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const db = await getDbSafe();
      const snap = await getDocs(collection(db, 'events'));
      const rows: AfroEvent[] = snap.docs.map(d => {
        const data = d.data() as any;
        // Parse foodSections back from JSON string if stored as string
        let foodSections: FoodSection[] | undefined;
        if (data.foodSections) {
          try {
            foodSections = typeof data.foodSections === 'string'
              ? JSON.parse(data.foodSections)
              : data.foodSections;
          } catch { /* ignore */ }
        }
        return {
          id: d.id,
          title: '',
          description: '',
          imageUrl: '',
          status: 'active' as const,
          createdBy: '',
          ...data,
          foodSections,
          startsAt: toIso(data.startsAt),
          expiresAt: toIso(data.expiresAt),
        };
      });
      rows.sort((a, b) => {
        // Sort by order first (ascending, nulls at the end)
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        // Fallback to start/expiry date (descending)
        return (b.startsAt || b.expiresAt) > (a.startsAt || a.expiresAt) ? 1 : -1;
      });
      setEvents(rows);
    } catch (e: any) {
      console.error('[LegionEventManager] fetch error:', e);
      toast.error(`Could not load events: ${e?.message || 'unknown error'}`);
    } finally {
      setLoadingEvents(false);
    }
  };

  // ─── Reorder ──────────────────────────────────────────────────────────────────
  const handleReorder = async (newOrder: AfroEvent[]) => {
    setEvents(newOrder);
    try {
      const db = await getDbSafe();
      const batch = writeBatch(db);
      newOrder.forEach((event, index) => {
        const eventRef = doc(db, 'events', event.id);
        batch.update(eventRef, { order: index });
      });
      await batch.commit();
      console.log('[LegionEventManager] Order updated');
    } catch (e) {
      console.error('[LegionEventManager] Could not save order:', e);
      toast.error('Failed to sync new order to server');
    }
  };

  useEffect(() => {
    if (isOpen) fetchEvents();
  }, [isOpen]);

  // ─── Image (Firebase Storage Upload) ──────────────────────────────────────────
  const handleImageFile = async (file: File) => {
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be < 8 MB'); return; }
    setImgUploading(true);
    try {
      const storage = await getStorageSafe();
      // Create a unique filename in the events folder
      const filename = `events/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, filename);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      
      // Get the public download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      setEditingEvent(prev => ({ ...prev, imageUrl: downloadURL }));
    } catch (e: any) {
      console.error('Image upload failed:', e);
      toast.error('Failed to upload image to Storage');
    } finally {
      setImgUploading(false);
    }
  };

  // ─── Food name change: update title + section headings ───────────────────────
  const handleFoodNameChange = (name: string) => {
    setEditingEvent(prev => ({
      ...prev,
      foodName: name,
      title: `Food of the Week: ${name}`,
      foodSections: (prev?.foodSections || DEFAULT_FOOD_SECTIONS()).map((s, i) =>
        i === 0 ? { ...s, heading: `About ${name || '[Food Name]'}` } : s
      ),
    }));
  };

  // ─── Section content change ───────────────────────────────────────────────────
  const updateSection = (idx: number, field: keyof FoodSection, value: string) => {
    setEditingEvent(prev => {
      const sections = [...(prev?.foodSections || [])];
      if (!sections[idx]) sections[idx] = { emoji: '✨', heading: '', content: '' };
      sections[idx] = { ...sections[idx], [field]: value };
      return { ...prev, foodSections: sections };
    });
  };

  const addCustomSection = () => {
    setEditingEvent(prev => {
      const sections = [...(prev?.foodSections || [])];
      if (sections.length < 4) {
        sections.push({ emoji: '⭐', heading: 'Custom Header', content: '' });
      }
      return { ...prev, foodSections: sections };
    });
  };

  const removeCustomSection = (idx: number) => {
    setEditingEvent(prev => {
      const sections = [...(prev?.foodSections || [])];
      sections.splice(idx, 1);
      return { ...prev, foodSections: sections };
    });
  };

  // ─── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editingEvent?.title?.trim()) {
      alert('Title is required! Please scroll to the top of the form to fill it out.');
      document.getElementById('legion-modal-top')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setSaving(true);
    try {
      const db = await getDbSafe();
      const payload: any = {
        title: editingEvent.title.trim(),
        description: editingEvent.description?.trim() || '',
        imageUrl: editingEvent.imageUrl || '',
        template: editingEvent.template || 'custom',
        foodName: editingEvent.foodName || '',
        // Store sections as JSON string (Firestore has no array-of-objects indexing issues this way)
        foodSections: editingEvent.foodSections
          ? JSON.stringify(editingEvent.foodSections)
          : null,
        // Always set startsAt to NOW
        startsAt: serverTimestamp(),
        // Expire at the YapDay cutoff for the chosen date
        expiresAt: editingEvent.expiresAt
          ? Timestamp.fromDate(getYapDayEndForDate(editingEvent.expiresAt))
          : Timestamp.fromDate(getYapDayEndForDate(defaultExpiryDate())),
        status: 'active',
        updatedAt: serverTimestamp(),
        updatedBy: moderatorId,
      };
      if (isNew) {
        await addDoc(collection(db, 'events'), {
          ...payload,
          createdBy: moderatorId,
          createdAt: serverTimestamp(),
        });
        toast.success('Event published ✓');
      } else {
        await updateDoc(doc(db, 'events', editingEvent.id!), payload);
        toast.success('Event updated ✓');
      }
      setEditingEvent(null);
      await fetchEvents();
    } catch (e: any) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Archive / Delete / Restore ───────────────────────────────────────────────
  const handleArchive = async (id: string) => {
    if (!window.confirm('Archive? Hidden from Explore.')) return;
    try {
      const db = await getDbSafe();
      await updateDoc(doc(db, 'events', id), { status: 'archived', archivedBy: moderatorId, archivedAt: serverTimestamp() });
      toast.success('Archived');
      fetchEvents();
    } catch { toast.error('Archive failed'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Permanently delete this event?')) return;
    try {
      const db = await getDbSafe();
      await deleteDoc(doc(db, 'events', id));
      toast.success('Deleted');
      fetchEvents();
    } catch { toast.error('Delete failed'); }
  };

  const handleRestore = async (id: string) => {
    try {
      const db = await getDbSafe();
      await updateDoc(doc(db, 'events', id), { status: 'active' });
      toast.success('Restored');
      fetchEvents();
    } catch { toast.error('Restore failed'); }
  };

  // ─── Status badge ─────────────────────────────────────────────────────────────
  const getStatus = (e: AfroEvent) => {
    if (e.status === 'archived') return { icon: '🗄', label: 'Archived', borderColor: 'rgba(255,255,255,0.06)' };
    if (e.expiresAt && new Date(e.expiresAt) < new Date()) return { icon: '⏰', label: 'Expired', borderColor: 'rgba(239,68,68,0.2)' };
    return { icon: '✅', label: 'Active', borderColor: 'rgba(99,102,241,0.25)' };
  };

  const isFoodWeek = editingEvent?.template === 'food_of_week';
  const isCustomEvent = editingEvent?.template === 'custom';
  const canHaveSections = isFoodWeek || isCustomEvent;

  if (!isOpen || typeof document === 'undefined') return null;

  // ─── Portal ───────────────────────────────────────────────────────────────────
  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={e => { if (e.target === e.currentTarget) { setEditingEvent(null); onClose(); } }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 16 }}
        transition={{ type: 'spring', damping: 26, stiffness: 320 }}
        className="relative w-[92vw] max-w-sm rounded-[24px] overflow-hidden shadow-2xl flex flex-col"
        style={{ backgroundColor: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-b shrink-0"
          style={{ backgroundColor: '#161616', borderColor: 'rgba(255,255,255,0.07)' }}
        >
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.35),rgba(139,92,246,0.25))', border: '1px solid rgba(99,102,241,0.4)' }}
          >
            <ShieldAlert className="w-4 h-4 text-indigo-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white leading-tight">Legion Remote</p>
            <p className="text-[10px] text-white/35 leading-tight">Manage Explore Events</p>
          </div>
          <button
            onClick={() => { setEditingEvent(null); onClose(); }}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
          >
            <X className="w-3.5 h-3.5 text-white/50" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 min-h-0">
          <AnimatePresence mode="wait">

            {/* ══ FORM ══ */}
            {editingEvent ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-4 space-y-3"
              >
                <div id="legion-modal-top" className="flex items-center justify-between">
                  <p className="text-sm font-bold text-white">{isNew ? 'New Event' : 'Edit Event'}</p>
                  <button onClick={() => setEditingEvent(null)} className="text-[11px] text-white/35 hover:text-white/60 transition-colors">← Back</button>
                </div>

                {isFoodWeek && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <Utensils className="w-3 h-3 text-amber-400 shrink-0" />
                    <span className="text-[11px] font-semibold text-amber-300">Food of the Week Template</span>
                  </div>
                )}

                {/* Banner */}
                <div>
                  <p className="text-[10px] font-bold text-white/35 uppercase tracking-widest mb-1.5">Banner Image</p>
                  <div
                    className="relative h-32 rounded-xl overflow-hidden border-2 border-dashed border-white/8 cursor-pointer hover:border-indigo-500/35 transition-colors bg-white/4 flex items-center justify-center"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {editingEvent.imageUrl ? (
                      <>
                        <img src={editingEvent.imageUrl} alt="banner" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs font-bold">Change Image</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-white/20 select-none">
                        <ImageIcon className="w-7 h-7" />
                        <span className="text-xs">Tap to upload banner</span>
                      </div>
                    )}
                    {imgUploading && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleImageFile(f); e.target.value = ''; }}
                    />
                  </div>
                </div>

                {/* ── Food name (auto-fills title + first section heading) ── */}
                {isFoodWeek && (
                  <div>
                    <p className="text-[10px] font-bold text-white/35 uppercase tracking-widest mb-1.5">
                      Food Name <span className="text-amber-400 normal-case">*</span>
                    </p>
                    <Input
                      value={editingEvent.foodName || ''}
                      onChange={e => handleFoodNameChange(e.target.value)}
                      placeholder="e.g. Jollof Rice, Fufu, Injera..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-9 text-sm"
                      maxLength={40}
                    />
                    <p className="text-[9px] text-white/25 mt-0.5">Auto-fills title as "Food of the Week: [name]"</p>
                  </div>
                )}

                {/* Title */}
                <div>
                  <p className="text-[10px] font-bold text-white/35 uppercase tracking-widest mb-1.5">
                    Title <span className="text-red-400 normal-case">*</span>
                  </p>
                  <Input
                    value={editingEvent.title || ''}
                    onChange={e => setEditingEvent(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={isFoodWeek ? 'Auto-filled above' : 'Event title'}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-9 text-sm"
                    maxLength={80}
                    readOnly={isFoodWeek && !!(editingEvent.foodName)}
                  />
                </div>

                {/* Subtitle / one-liner */}
                <div>
                  <p className="text-[10px] font-bold text-white/35 uppercase tracking-widest mb-1.5">
                    {isFoodWeek ? 'One-line Subtitle' : 'Description'}
                  </p>
                  <Input
                    value={editingEvent.description || ''}
                    onChange={e => setEditingEvent(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={isFoodWeek ? 'e.g. A traditional spiced rice from the Swahili Coast' : 'Short description'}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-9 text-sm"
                    maxLength={140}
                  />
                </div>

                {/* ══ Event structured sections ══ */}
                {canHaveSections && (
                  <div className="pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-bold text-white/35 uppercase tracking-widest">
                        Event Sections
                      </p>
                      <button
                        onClick={addCustomSection}
                        className="text-[10px] text-indigo-300 hover:text-indigo-200 transition-colors font-bold flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Section
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {(editingEvent.foodSections || []).map((section, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl overflow-hidden border border-white/8 relative"
                          style={{ backgroundColor: '#1a1a1a' }}
                        >
                          {/* Section header — emoji & heading */}
                          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/6 pr-8">
                            <input
                              value={section.emoji}
                              onChange={e => updateSection(idx, 'emoji', e.target.value)}
                              className="w-8 text-center bg-white/5 border border-white/10 rounded text-white outline-none text-base"
                              maxLength={2}
                              title="Change emoji"
                            />
                            {isFoodWeek && idx === 0 ? (
                               <p className="text-xs font-semibold text-white/70 flex-1">{section.heading}</p>
                            ) : (
                               <input
                                 value={section.heading}
                                 onChange={e => updateSection(idx, 'heading', e.target.value)}
                                 placeholder="Section Heading"
                                 className="text-xs font-semibold text-white/70 flex-1 bg-transparent outline-none placeholder:text-white/40"
                                 maxLength={120}
                               />
                            )}
                          </div>
                          {/* Remove button */}
                          {(!isFoodWeek || idx > 0) && (
                            <button
                              onClick={() => removeCustomSection(idx)}
                              className="absolute top-2 right-2 p-1 hover:bg-white/10 rounded-md transition-colors"
                              title="Remove Section"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          )}
                          {/* Section body */}
                          <Textarea
                            value={section.content}
                            onChange={e => updateSection(idx, 'content', e.target.value)}
                            placeholder={
                              isFoodWeek
                                ? (idx === 0 ? `What is ${editingEvent.foodName || 'this food'}? History, flavors...` :
                                   idx === 1 ? 'Steps / bullet lines. Start each with • for bullets' :
                                   idx === 2 ? 'Cultural origins, influences, when it is served...' :
                                   'Countries or regions where it is popular...')
                                : 'Write section content here... Start with • for bullets'
                            }
                            className="bg-transparent border-0 text-white placeholder:text-white/20 resize-none text-sm rounded-none focus-visible:ring-0"
                            rows={5}
                            maxLength={10000}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Schedule info block */}
                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-3">
                  <p className="text-[10px] font-bold text-indigo-300 uppercase tracking-widest mb-1.5">
                    Expires On (YapDay) <span className="text-red-400 normal-case">*</span>
                  </p>
                  <input
                    type="date"
                    value={editingEvent.expiresAt?.split('T')[0] || defaultExpiryDate()}
                    onChange={e => setEditingEvent(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="w-full rounded-xl px-2.5 py-2 mb-2 text-xs text-white bg-black/20 border border-indigo-500/30 outline-none focus:border-indigo-400 transition-colors"
                  />
                  <p className="text-[10px] text-indigo-200/70 leading-relaxed">
                    Event goes live <strong>immediately</strong>. It will expire at the exact end of the YapDay for the date selected above: <strong>{getYapDayEndForDate(editingEvent.expiresAt || defaultExpiryDate()).toLocaleString()}</strong>.
                  </p>
                </div>

                {/* Footer */}
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="ghost"
                    onClick={() => setEditingEvent(null)}
                    disabled={saving}
                    className="flex-1 text-white/40 hover:text-white hover:bg-white/5 rounded-xl h-9 text-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 text-white rounded-xl h-9 text-sm font-semibold disabled:opacity-30"
                    style={{ background: saving ? '#444' : 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
                  >
                    {saving
                      ? <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      : isNew ? 'Publish' : 'Save'
                    }
                  </Button>
                </div>
              </motion.div>

            ) : (
              /* ══ LIST ══ */
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-5 pb-8 space-y-4"
              >
                <div className="grid grid-cols-2 gap-2">
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    onClick={() => { setIsNew(true); setEditingEvent(EMPTY_EVENT()); }}
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-indigo-500/25 bg-indigo-500/8 text-indigo-300 hover:bg-indigo-500/15 transition-colors text-xs font-semibold"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Custom Event
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    onClick={() => { setIsNew(true); setEditingEvent(FOOD_TEMPLATE()); }}
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-amber-500/25 bg-amber-500/8 text-amber-300 hover:bg-amber-500/15 transition-colors text-xs font-semibold"
                  >
                    <Utensils className="w-3.5 h-3.5" />
                    Food of Week
                  </motion.button>
                </div>

                <button
                  onClick={fetchEvents}
                  disabled={loadingEvents}
                  className="w-full flex items-center justify-center gap-1.5 text-[10px] text-white/25 hover:text-white/45 transition-colors py-1"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingEvents ? 'animate-spin' : ''}`} />
                  Refresh list
                </button>

                {/* Gold Currency Info */}
                <div className="pt-2">
                  <div className="flex items-start gap-2.5 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="p-1.5 rounded-full bg-amber-500/20 text-amber-500">
                      <Coins className="w-3.5 h-3.5" />
                    </div>
                    <p className="text-[10px] leading-relaxed text-white font-medium">
                      Gold is the In-App currency that powers community building on this app. 
                      You can create forums (500), create tribes (1000), add friends (10).
                    </p>
                  </div>
                </div>

                {loadingEvents ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-white/25" />
                  </div>
                ) : events.length === 0 ? (
                  <div className="text-center py-10">
                    <Calendar className="w-9 h-9 mx-auto mb-2 text-white/15" />
                    <p className="text-sm text-white/30">No events yet</p>
                    <p className="text-[10px] text-white/20 mt-1">Use the buttons above to create one</p>
                  </div>
                ) : (
                  <Reorder.Group axis="y" values={events} onReorder={handleReorder} className="space-y-2">
                    {events.map(event => {
                      const badge = getStatus(event);
                      const isExpanded = expandedId === event.id;
                      return (
                        <Reorder.Item
                          key={event.id}
                          value={event}
                          className="rounded-xl overflow-hidden select-none"
                          style={{ border: `1px solid ${badge.borderColor}`, backgroundColor: '#181818' }}
                        >
                          <div className="flex items-center gap-2.5 p-2.5">
                            {/* Drag Handle */}
                            {events.length > 1 && (
                                <div className="cursor-grab active:cursor-grabbing p-1 text-white/10 hover:text-white/30 transition-colors">
                                    <GripVertical className="w-3.5 h-3.5" />
                                </div>
                            )}
                            
                            <div className="w-12 h-9 rounded-lg overflow-hidden shrink-0 bg-white/5 flex items-center justify-center">
                              {event.imageUrl
                                ? <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover" />
                                : <Calendar className="w-3.5 h-3.5 text-white/20" />
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-white truncate">{event.title}</p>
                              <p className="text-[10px] text-white/30">
                                {badge.icon} {badge.label}
                                {event.expiresAt ? ` · ${new Date(event.expiresAt).toLocaleDateString()}` : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : event.id)}
                                className="p-1.5 rounded-lg hover:bg-white/8 transition-colors"
                              >
                                {isExpanded
                                  ? <ChevronUp className="w-3.5 h-3.5 text-white/25" />
                                  : <ChevronDown className="w-3.5 h-3.5 text-white/25" />
                                }
                              </button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-1.5 rounded-lg hover:bg-white/8 transition-colors">
                                    <MoreVertical className="w-3.5 h-3.5 text-white/35" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  style={{ backgroundColor: '#212121', zIndex: 200000 }}
                                  className="min-w-[130px] rounded-xl border border-white/10"
                                >
                                  <DropdownMenuItem
                                    className="text-indigo-300 cursor-pointer rounded-lg focus:bg-indigo-500/10 text-xs"
                                    onSelect={() => { setIsNew(false); setEditingEvent({ ...event }); }}
                                  >
                                    <Edit2 className="w-3 h-3 mr-2" /> Edit
                                  </DropdownMenuItem>
                                  {event.status === 'archived' ? (
                                    <DropdownMenuItem
                                      className="text-green-400 cursor-pointer rounded-lg focus:bg-green-500/10 text-xs"
                                      onSelect={() => handleRestore(event.id)}
                                    >
                                      <Calendar className="w-3 h-3 mr-2" /> Restore
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      className="text-amber-400 cursor-pointer rounded-lg focus:bg-amber-500/10 text-xs"
                                      onSelect={() => handleArchive(event.id)}
                                    >
                                      <Archive className="w-3 h-3 mr-2" /> Archive
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator className="bg-white/8" />
                                  <DropdownMenuItem
                                    className="text-red-400 cursor-pointer rounded-lg focus:bg-red-500/10 text-xs"
                                    onSelect={() => handleDelete(event.id)}
                                  >
                                    <Trash2 className="w-3 h-3 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                className="overflow-hidden"
                              >
                                <div className="px-3 pb-3 pt-2 border-t border-white/5 space-y-1">
                                  {event.description && <p className="text-[11px] text-white/50 italic">{event.description}</p>}
                                  {event.foodSections && event.foodSections.length > 0 && (
                                    <p className="text-[10px] text-amber-400/60">
                                      {event.foodSections.length} sections · {event.foodSections.filter(s => s.content).length} filled
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-3 text-[10px] text-white/20 pt-1">
                                    {event.startsAt && <span>Starts: {new Date(event.startsAt).toLocaleString()}</span>}
                                    {event.expiresAt && <span>Expires: {new Date(event.expiresAt).toLocaleString()}</span>}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Reorder.Item>
                      );
                    })}
                  </Reorder.Group>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
